import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  CircularProgress,
  Box,
  Typography,
  Paper,
  Link,
  Grid,
  useMediaQuery,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from "@mui/material";
import Lottie from "react-lottie";
import resultAnimationData from "../components/animations/0.json";
import apis from "../utils/apis";
import { useSelector, useDispatch } from "react-redux";
import ResultCarousel from "../page/ResultPageCarousel";
import Header from "../components/Header";
import { setResumeCarousel } from "../redux/action/result";
import { fetchResumeQuestionJson } from "../components/stepper_form/stepper.helper";
import { setResumeQuestionJson } from "../redux/action/resumeCarouselActions";
import { useFeatureFlag } from "@harnessio/ff-react-client-sdk";
import ReactGA4 from "react-ga4";
import { setUserStatus } from "../redux/action/user";
import { setLoading } from "../redux/action/loadingRequest";
import { getEmail } from "../utils/storage";
import { useNavigate } from "react-router-dom";

// Initialize ReactGA4
// ReactGA4.initialize("YOUR_GA4_MEASUREMENT_ID");
import Loading from "./common/Loading";

const ResumeUploadRecruiter = ({
  secretEncodeDecodeString,
  staticRandomEncodeDecodeString,
}) => {
  const navigate = useNavigate(); // Initialize useNavigate
  const ResumeAcceptedValidityPeriod = useFeatureFlag(
    "Resume_Accepted_Validity_Period"
  );
  const [file, setFile] = useState([]); // Ensure file is initialized as an empty array
  const [isUploading, setIsUploading] = useState(false);
  const [isFetchingResult, setIsFetchingResult] = useState(false);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null); // New state for warnings
  const [convertedMarketSalary, setConvertedMarketSalary] = useState(null);
  const [convertedTechValue, setConvertedTechvalue] = useState(null);
  const [uuid, setUuid] = useState(null);
  const [currentStep, setCurrentStep] = useState(0); // 0: Upload, 1: Form, 2: Result
  const [parsedResume, setParsedResume] = useState(null); // Parsed resume data
  const [formData, setFormData] = useState({}); // Form data from /get-updated-form
  const [formErrors, setFormErrors] = useState({}); // Track validation errors
  const [updatedData, setUpdatedData] = useState();
  const isMobile = useMediaQuery("(max-width:600px)");
  const shouldRunEffect = useRef(true);
  const [locationCorrect, setLocationCorrect] = useState(false);
  const isLoggedIn = useSelector(
    (state) => state.userReducer.isLoggedIn || false
  );
  const [uploadedFiles, setUploadedFiles] = useState([]); // State for uploaded files
  const [showUploadedView, setShowUploadedView] = useState(false); // State to toggle UI
  const [loadingFiles, setLoadingFiles] = useState({}); // Track loading state for individual files
  const userRefId = useSelector((state) => state.userReducer.userRefId);
  const userEmail = getEmail();
  const maxRetries = 2;

  const dispatch = useDispatch();
  const resultData = useSelector(
    (state) => state?.ResultReducer?.resumeCarousel || []
  );
  const resumeQuestionObject = useSelector(
    (state) =>
      state?.ResumeQuestionJsonReducer?.resumeQuestionObject?.questionList || []
  );
  // Handlers
  const isFileRecent = (file) => {
    const currentDate = new Date(); // Current system date
    const monthsAgo = new Date(currentDate);

    // Use the harness flag value, defaulting to 6 if the flag is not available
    const validityPeriod = ResumeAcceptedValidityPeriod || 6;
    monthsAgo.setMonth(currentDate.getMonth() - validityPeriod);

    const fileModifiedDate = new Date(file.lastModified);
    return fileModifiedDate >= monthsAgo;
  };

  // Handle file selection
  const handleFileChange = (e) => {
    dispatch(setResumeCarousel([]));
    setConvertedMarketSalary(null);
    setConvertedTechvalue(null);
    const selectedFiles = Array.from(e.target.files);
    const validExtensions = ["pdf", "zip"];
    const maxFileSizeMB = 2;
    const maxFiles = 25;

    const invalidFiles = selectedFiles.filter((file) => {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      const fileSizeMB = file.size / (1024 * 1024);
      return (
        !validExtensions.includes(fileExtension) || fileSizeMB > maxFileSizeMB
      );
    });

    if (invalidFiles.length > 0) {
      setError(
        `Invalid files detected. Only .pdf files under ${maxFileSizeMB} MB are allowed.`
      );
      setWarning(null);
      setFile([]); // Reset file to an empty array
    } else if ((file?.length || 0) + selectedFiles.length > maxFiles) {
      setError(`You can only upload up to ${maxFiles} files.`);
      setWarning(null);
    } else {
      setFile((prevFiles) => [...(prevFiles || []), ...selectedFiles]); // Append new files to the existing state
      setError(null);
      setWarning(null);
    }
  };

  // Handle drag-and-drop
  const handleDrop = (e) => {
    dispatch(setResumeCarousel([]));
    e.preventDefault();
    const selectedFile = e.dataTransfer.files[0];
    if (selectedFile) {
      const fileExtension = selectedFile.name.split(".").pop().toLowerCase();
      const fileSizeMB = selectedFile.size / (1024 * 1024);
      const validityPeriod = ResumeAcceptedValidityPeriod || 6;

      if (!["pdf"].includes(fileExtension)) {
        setError("Only .pdf files are allowed.");
        setWarning(null);
        setFile([]); // Reset file to an empty array instead of null
      } else if (fileSizeMB > 2) {
        setError("File size must be smaller than 2 MB.");
        setWarning(null);
        setFile([]); // Reset file to an empty array instead of null
      } else {
        setFile([selectedFile]); // Wrap the single file in an array
        setError(null);
        if (!isFileRecent(selectedFile)) {
          setWarning(
            `Warning: Your resume is older than ${validityPeriod} months. Results may not be accurate.`
          );
        } else {
          setWarning(null);
        }
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // Encryption function
  function DataEncryption(json) {
    const jsonString = JSON.stringify(json);
    const customStringJson =
      secretEncodeDecodeString + jsonString + secretEncodeDecodeString;
    const newCustomStringJson = customStringJson.replace(
      /"/g,
      staticRandomEncodeDecodeString
    );
    const encodedString = btoa(newCustomStringJson);
    return encodedString;
  }

  // Decryption function
  function DataDecryption(jsonString) {
    try {
      const decodedString = atob(jsonString);
      const getOldStringOne = decodedString.replace(
        new RegExp(secretEncodeDecodeString, "g"),
        ""
      );
      const str = staticRandomEncodeDecodeString;
      const escapedString = str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const regex = new RegExp(escapedString, "g");
      const getOldStringTwo = getOldStringOne.replace(regex, '"');
      return JSON.parse(getOldStringTwo);
    } catch (error) {
      throw new Error("Decryption failed. Invalid response structure.");
    }
  }

  // Fetch skill validity
  const fetchSkillValidity = async (skill) => {
    let retryCount = 0;
    while (retryCount <= maxRetries) {
      try {
        const encryptedPayload = DataEncryption({ primarySkill: skill });
        const response = await apis.POST({
          endpoint: "/get-topSkillValidity",
          payload: encryptedPayload,
          options: { type: "json" },
        });
        if (!response.data)
          throw new Error("No data received from skill validity API.");
        const decryptedData = DataDecryption(response.data);
        return decryptedData;
      } catch (error) {
        const status = error?.response?.status || error?.status;
        if ((status === 504 || status === 502) && retryCount < maxRetries) {
          retryCount++;
          const delay = 1000 * Math.pow(2, retryCount); // Exponential backoff
          console.log(
            `Result attempt ${retryCount} failed with ${status}. Retrying in ${delay}ms...`
          );
          await new Promise((resolve) => setTimeout(resolve, delay));
        } else {
          throw new Error(
            "We couldn't validate your skills due to a server issue. Please try again later."
          ); // Throw if not 504 or max retries reached
        }
      }
    }
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(",")[1]); // Remove Base64 header
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });

  // Handle resume upload and parsing
  const handleUpload = async () => {
    ReactGA4.event({
      category: "button",
      action: "click",
      label: "Upload Resume",
    });

    if (!file || file.length === 0) {
      setError("Please select at least one file.");
      return;
    }

    setIsUploading(true);
    setError(null);

    // Add retry logic for /upload-resumes
    const uploadResumesWithRetry = async (retryCount = 0) => {
      try {
        const formData = {};
        // Prepare resumes input
        formData.files = await Promise.all(
          file.map(async (f) => ({
            fileName: f.name,
            fileContent: await fileToBase64(f),
          }))
        );
        return await apis.POST({
          endpoint: "/upload-resumes",
          payload: formData,
          options: { type: "json" },
        });
      } catch (err) {
        const status = err?.response?.status || err?.status;
        if ((status === 502 || status === 504) && retryCount < 2) {
          return uploadResumesWithRetry(retryCount + 1);
        }
        throw err;
      }
    };

    try {
      const response = await uploadResumesWithRetry();
      const data = response.data.body;

      // Update state with uploaded files and show the uploaded view
      if (Array.isArray(data)) {
        setUploadedFiles(data); // Store uploaded files in state
        setShowUploadedView(true); // Show the new UI
      } else {
        setError("Unexpected response format from the server.");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to process the resumes. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleProceed = async (file) => {
    setLoadingFiles((prev) => ({ ...prev, [file.fileUrl]: true })); // Use fileUrl as key

    try {
      // Add retry logic for /process-resumes
      const processResumesWithRetry = async (retryCount = 0) => {
        try {
          return await apis.POST({
            endpoint: "/process-resumes",
            payload: { fileName: file.fileName, fileUrl: file.fileUrl },
            options: { type: "json" },
          });
        } catch (err) {
          const status = err?.response?.status || err?.status;
          if ((status === 502 || status === 504) && retryCount < 2) {
            return processResumesWithRetry(retryCount + 1);
          }
          throw err;
        }
      };

      // Call "process-resumes" API with retry
      const processResponse = await processResumesWithRetry();

      if (
        !processResponse.data ||
        !processResponse.data.processedResume.extractedText
      ) {
        throw new Error("Failed to process the resume.");
      }
      let pdfText = processResponse.data.processedResume.extractedText;

      // Call "get-parsed-resume" API with retry logic
      const getParsedResumeWithRetry = async (retryCount = 0) => {
        try {
          return await apis.POST({
            endpoint: "/get-parsed-resume",
            payload: {
              pdfText: processResponse.data.processedResume.extractedText,
            },
            options: { type: "json" },
          });
        } catch (err) {
          const status = err?.response?.status || err?.status;
          if ((status === 502 || status === 504) && retryCount < 2) {
            return getParsedResumeWithRetry(retryCount + 1);
          }
          throw err;
        }
      };

      const parsedResponse = await getParsedResumeWithRetry();

      if (!parsedResponse.data) {
        throw new Error("Failed to parse the resume.");
      }

      let parsedResumeData = JSON.parse(parsedResponse.data.gptResponse);
      parsedResumeData.pdfText = pdfText;

      // Call "get-updated-form" API with retry logic
      const getUpdatedFormWithRetry = async (retryCount = 0) => {
        try {
          return await apis.POST({
            endpoint: "/get-updated-form",
            payload: parsedResumeData, // Include parsed resume data as payload
            options: { type: "json" },
          });
        } catch (err) {
          const status = err?.response?.status || err?.status;
          if ((status === 502 || status === 504) && retryCount < 2) {
            return getUpdatedFormWithRetry(retryCount + 1);
          }
          throw err;
        }
      };

      const updatedFormResponse = await getUpdatedFormWithRetry();

      if (!updatedFormResponse.data) {
        throw new Error("Failed to fetch updated form data.");
      }
      const processSkills = async (skillType, skillData) => {
        if (Array.isArray(skillData) && skillData.length > 0) {
          try {
            const validatedSkill = await fetchSkillValidity(skillData);
            if (validatedSkill && validatedSkill.topPrimarySkill) {
              updatedFormResponse.data.userForm[skillType] =
                validatedSkill.topPrimarySkill;
            } else {
              console.warn(`Invalid response for ${skillType} validity.`);
            }
          } catch (error) {
            console.error(
              `Error fetching ${skillType} validity:`,
              error.message
            );
          }
        } else {
          console.warn(`${skillType} is not a valid array or is empty.`);
        }
      };
      console.log(updatedFormResponse.data, "updatedFormResponse.data");

      await processSkills(
        "primarySkill",
        updatedFormResponse.data.userForm.primarySkill
      );
      await processSkills(
        "secondarySkill",
        updatedFormResponse.data.userForm.secondarySkill
      );

      // Add meaningful information to the file object for display
      const updatedFileInfo = {
        ...file,
        status: "Uploaded",
        parsedData: parsedResumeData,
        updatedForm: updatedFormResponse.data,
      };

      // Update the uploaded files state with the new information
      setUploadedFiles((prevFiles) =>
        prevFiles.map((f) => (f.fileUrl === file.fileUrl ? updatedFileInfo : f))
      );

      // Call "get-result" API with retry logic
      // Define resultPayload before using it in getResultWithRetry
      const resultPayload = {
        ...updatedFormResponse.data.userForm,
        userRefId: userRefId,
        recruiterEmail: userEmail,
        uploadedByRecruiter: true,
        fileName: file.fileName,
        fileUrl: file.fileUrl,
        payloadType: "carousel-resume",
      };

      const getResultWithRetry = async (retryCount = 0) => {
        try {
          return await apis.POST({
            endpoint: "/get-result",
            payload: resultPayload,
            options: { type: "json" },
          });
        } catch (err) {
          const status = err?.response?.status || err?.status;
          if ((status === 502 || status === 504) && retryCount < 2) {
            return getResultWithRetry(retryCount + 1);
          }
          throw err;
        }
      };

      const resultResponse = await getResultWithRetry();

      if (!resultResponse.data) {
        throw new Error("Failed to fetch result data.");
      }

      // Add uuid from the result response to the file object
      const updatedFileInfoWithUuid = {
        ...file,
        status: "Completed",
        parsedData: parsedResumeData,
        updatedForm: updatedFormResponse.data,
        uuid: resultResponse.data.uuid, // Add uuid for navigation
      };

      // Update the uploaded files state with the new information
      setUploadedFiles((prevFiles) =>
        prevFiles.map((f) =>
          f.fileUrl === file.fileUrl ? updatedFileInfoWithUuid : f
        )
      );
      console.log(resultResponse.data, "resultResponse");
      console.log("Result Data:", resultResponse.data);
      console.log(updatedFormResponse.data, "updatedFormResponse");

      // Handle successful response (e.g., navigate to another page or update state)
      console.log("Parsed Resume Data:", parsedResponse.data);
      console.log("Updated Form Data:", updatedFormResponse.data);
    } catch (error) {
      console.error(error);
      setError("Failed to process the resume. Please try again.");
    } finally {
      setLoadingFiles((prev) => ({ ...prev, [file.fileUrl]: false })); // Use fileUrl as key
    }
  };

  // Convert months to years and months string Helper functions
  const convertMonthsToYearsAndMonths = (months) => {
    const totalMonths = parseInt(months, 10);
    if (isNaN(totalMonths) || totalMonths < 0) return "";
    if (totalMonths === 0) return "Fresher/Intern/Trainee";
    const years = Math.floor(totalMonths / 12);
    const remainingMonths = totalMonths % 12;
    let result = "";
    if (years > 0) {
      result += `${years} ${years > 1 ? "years" : "year"}`;
    }
    if (remainingMonths > 0) {
      result += `${years > 0 ? " " : ""}${remainingMonths} ${
        remainingMonths > 1 ? "months" : "month"
      }`;
    }
    return result.trim() || "0 months";
  };

  // Optional: Add an emoji based on experience level
  const getExperienceEmoji = (months) => {
    const totalMonths = parseInt(months, 10);
    if (isNaN(totalMonths)) return "";
    if (totalMonths === 0) return "🎓 ";
    const years = Math.floor(totalMonths / 12);
    if (years >= 5) return "🌟 ";
    if (years >= 2) return "🔥 ";
    if (years > 0) return "👍 ";
    const remainingMonths = totalMonths % 12;
    if (remainingMonths >= 6) return "✨ ";
    if (remainingMonths > 0) return "😊 ";
    return "";
  };

  // Validate a single field
  const validateField = (questionName, value) => {
    const question = resumeQuestionObject.find(
      (q) => q.questionName === questionName
    );
    if (!question) return;

    const errors = { ...formErrors };
    if (question.rules.required && (!value || value === "" || value === null)) {
      errors[questionName] = `${question.label} is required`;
    } else if (
      question.questionName === "experience" &&
      value &&
      (isNaN(value) || parseInt(value, 10) > 492)
    ) {
      errors[questionName] = isNaN(value)
        ? "Please enter a valid number"
        : "Experience cannot exceed 492 months (41 years)";
    } else {
      delete errors[questionName];
    }
    setFormErrors(errors);
  };

  // Validate entire form on submit
  const validateForm = () => {
    const errors = {};
    let allRequiredFilled = true;

    resumeQuestionObject.forEach((question) => {
      const value = formData[question.questionName];
      if (
        question.rules.required &&
        (!value || value === "" || value === null)
      ) {
        errors[question.questionName] = `${question.label} is required`;
        allRequiredFilled = false;
      } else if (
        question.questionName === "experience" &&
        value &&
        (isNaN(value) || parseInt(value, 10) > 492)
      ) {
        errors[question.questionName] = isNaN(value)
          ? "Please enter a valid number"
          : "Experience cannot exceed 492 months (41 years)";
      }
    });

    setFormErrors(errors);
    setError(allRequiredFilled ? null : "Please fill all required fields.");
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsFetchingResult(true);
    setError(null);
    try {
      const submittedData = { ...updatedData, ...formData };
      let resultResponse;
      const maxRetries = 2;
      let retryCount = 0;

      // Retry logic for /get-result
      while (retryCount <= maxRetries) {
        try {
          resultResponse = await apis.POST({
            endpoint: "/get-result",
            payload: submittedData,
            options: { type: "json" },
          });

          if (resultResponse.status === 200 || resultResponse.status === 201) {
            break; // Success, exit retry loop
          } else {
            throw new Error("Unexpected status code");
          }
        } catch (error) {
          const status = error?.response?.status || error?.status;
          if (status === 504 && retryCount < maxRetries) {
            retryCount++;
            const delay = 1000 * Math.pow(2, retryCount); // Exponential backoff
            console.log(
              `Result attempt ${retryCount} failed with 504. Retrying in ${delay}ms...`
            );
            await new Promise((resolve) => setTimeout(resolve, delay));
          } else {
            throw error; // Throw if not 504 or max retries reached
          }
        }
      }

      ReactGA4.event({
        category: "API",
        action: "api_call",
        label: "/get-result",
        value: "success",
      });

      if (!resultResponse.data) {
        throw new Error("No result data received.");
      }
      if (resultResponse.data.error) {
        throw new Error("Salary calculation failed");
      }

      const resultData = resultResponse.data;
      if (!resultData.convertedMarketSalaryRange) {
        throw new Error("No salary data available");
      }

      dispatch(setResumeCarousel(submittedData));
      setUuid(resultData.uuid);
      dispatch(
        setUserStatus({
          isLoggedIn:
            sessionStorage.getItem("isLoggedIn") === "true" ? true : false,
          userRefId: sessionStorage.getItem("userRefId"),
          loading: false,
          uuid: resultData.uuid,
          // isReferralUsers: object?.data?.isReferralUsers,
        })
      );
      setConvertedMarketSalary(resultData.convertedMarketSalaryRange);
      setConvertedTechvalue(resultData.convertedTechValue || null);
      setCurrentStep(2);
    } catch (error) {
      ReactGA4.event({
        category: "API",
        action: "api_call",
        label: "/get-result",
        value: "failure",
      });
      let errorMessage;
      switch (error.message) {
        case "No result data received.":
          errorMessage = "No response from the server. Please try again later.";
          break;
        case "Salary calculation failed":
          errorMessage =
            "We couldn't calculate your market salary at this time. Please try again later.";
          break;
        case "No salary data available":
          errorMessage =
            "We don't have enough data to calculate your salary range. Please try again later.";
          break;
        default:
          errorMessage =
            "Our servers are currently busy processing your results. Please try again in a few minutes.";
      }
      setError(errorMessage);
    } finally {
      setIsFetchingResult(false);
    }
  };

  // Handle back to upload
  const handleBackToUpload = () => {
    setIsUploading(false);
    setCurrentStep(0);
    setFile(null);
    setError(null);
    setWarning(null);
    setConvertedMarketSalary(null);
    setConvertedTechvalue(null);
    setUuid(null);
    setParsedResume(null);
    setFormData({});
    setFormErrors({});
    dispatch(setResumeCarousel([]));
  };

  // Clear selected files
  const handleClearFiles = () => {
    setFile([]); // Set file to an empty array instead of null
    setError(null);
    setWarning(null);
    setUploadedFiles([]);
    setShowUploadedView(false);
  };

  // Initial setup
  useEffect(() => {
    if (shouldRunEffect.current) {
      dispatch(setResumeCarousel([]));
      fetchResumeQuestionJson(dispatch, setResumeQuestionJson);
      shouldRunEffect.current = false;
    }
  }, [dispatch]);

  useEffect(() => {
    ReactGA4.send({
      hitType: "pageview",
      page: "/resume-upload",
      title: "visited resume upload",
    });
    if (isLoggedIn === false) {
      dispatch(setLoading(true));
    }
  }, []);

  return (
    <>
      {!isLoggedIn ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
            backgroundImage:
              "url('../../assets/images/homepage-background.svg')",
          }}
          position="fixed"
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            backgroundColor: "#000",
            minHeight: "100vh",
            width: "100%",
            color: "#FFF",
            backgroundImage:
              "url('../../assets/images/homepage-background.svg')",
            backgroundRepeat: "repeat",
            backgroundSize: "auto",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            paddingX: "20px",
          }}
        >
          <Header />

          {/* Step 0: Upload Resume */}
          {currentStep === 0 && (
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {showUploadedView ? (
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      textAlign: "center",
                      mt: { xs: 10, md: 3 },
                      mb: { xs: 2, md: 2 },
                      fontWeight: 700,
                      fontFamily: "Kanit",
                    }}
                  >
                    Uploaded Files
                  </Typography>
                  <TableContainer
                    sx={{
                      border: "1px solid #FDF9E0",
                      borderRadius: "8px",
                      maxWidth: {
                        xs: "100%",
                        md: "100%",
                        xl: "70%",
                      },
                      overflowX: "auto",
                      margin: "auto",
                    }}
                  >
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{ color: "#FDF9E0", fontWeight: "bold" }}
                          >
                            No
                          </TableCell>
                          <TableCell
                            sx={{ color: "#FDF9E0", fontWeight: "bold" }}
                          >
                            File Name
                          </TableCell>
                          <TableCell
                            sx={{ color: "#FDF9E0", fontWeight: "bold" }}
                          >
                            Status
                          </TableCell>
                          {/* <TableCell
                            sx={{ color: "#FDF9E0", fontWeight: "bold" }}
                          >
                            Parsed Data
                          </TableCell> */}
                          <TableCell
                            sx={{ color: "#FDF9E0", fontWeight: "bold" }}
                          >
                            Action
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {uploadedFiles.map((file, index) => (
                          <TableRow key={index}>
                            <TableCell sx={{ color: "#FDF9E0" }}>
                              {index + 1}
                            </TableCell>
                            <TableCell sx={{ color: "#FDF9E0" }}>
                              {file.fileName}
                            </TableCell>
                            <TableCell sx={{ color: "#FDF9E0" }}>
                              {file.status || "Pending"}
                            </TableCell>
                            <TableCell>
                              {file.status === "Completed" ? (
                                <Button
                                  variant="contained"
                                  color="primary"
                                  href={`/candidate-profile/${file.uuid}`}
                                  target="_blank"                                  
                                  sx={{
                                    color: "#FDF9E0",
                                    borderRadius: "8px",
                                    textTransform: "none",
                                  }}
                                >
                                  View
                                </Button>
                              ) : (
                                <Button
                                  variant="contained"
                                  color="primary"
                                  onClick={() => handleProceed(file)}
                                  disabled={loadingFiles[file.fileUrl]}
                                  sx={{
                                    "&.Mui-disabled": {
                                      backgroundColor: "#333",
                                      color: "#000",
                                    },
                                    color: "#FDF9E0",
                                    borderRadius: "8px",
                                    textTransform: "none",
                                  }}
                                >
                                  {loadingFiles[file.fileUrl]
                                    ? "Processing..."
                                    : "Process"}
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Box sx={{ textAlign: "center", my: 3 }}>
                    <Button
                      variant="contained"
                      onClick={() => setShowUploadedView(false)}
                      sx={{
                        fontSize: "14px",
                        textTransform: "none",
                        color: "#FDF9E0",
                        borderRadius: "8px",
                      }}
                    >
                      Upload More Resumes
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: 600,
                    margin: "auto",
                    padding: 3,
                  }}
                >
                  {!isUploading ? (
                    <Box>
                      <Paper
                        elevation={3}
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        sx={{
                          padding: { xs: "20px", md: "30px" },
                          textAlign: "center",
                          border: "1px dashed #FDF9E0",
                          borderRadius: { xs: "10px", md: "36px" },
                          backgroundColor: "transparent",
                        }}
                      >
                        <Box sx={{ p: 2, textAlign: "center" }}>
                          <Box
                            component="img"
                            sx={{
                              height: "100%",
                              width: "100%",
                              maxHeight: { xs: "80px", md: "146px" },
                              maxWidth: { xs: "80px", md: "168px" },
                            }}
                            alt="Upload Resume"
                            src="/assets/images/Upload-Resume.svg"
                          />
                        </Box>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontFamily: "Kanit",
                            marginBottom: "8px",
                            lineHeight: { xs: "26px", md: "58px" },
                            fontSize: { xs: "24px", md: "48px" },
                            color: "#FDF9E0",
                          }}
                        >
                          Upload Your Resumes
                        </Typography>
                        {file && file.length > 0 ? (
                          <Typography
                            sx={{
                              fontWeight: 500,
                              marginBottom: "44px",
                              lineHeight: { xs: "100%", md: "34px" },
                              fontSize: { xs: "14px", md: "22px" },
                              color: "#FDF9E0",
                            }}
                          >
                            Files Selected: {file.map((f) => f.name).join(", ")}
                          </Typography>
                        ) : (
                          <Typography
                            sx={{
                              fontWeight: 500,
                              marginBottom: "44px",
                              lineHeight: { xs: "100%", md: "34px" },
                              fontSize: { xs: "14px", md: "22px" },
                              color: "#FDF9E0",
                            }}
                          >
                            Drag, and Drop Files Here
                          </Typography>
                        )}
                        <Typography
                          component="label" // Ensures the text acts as a label for the file input
                          sx={{
                            fontWeight: 700,
                            lineHeight: "100%",
                            fontSize: { xs: "16px", md: "24px" },
                            cursor: isUploading ? "not-allowed" : "pointer", // Disable cursor if uploading
                            textDecoration: "underline",
                            color: "#FDF9E0",
                            opacity: isUploading ? 0.5 : 1, // Visual feedback when disabled
                          }}
                        >
                          Choose Multiple Files
                          <input
                            type="file"
                            hidden
                            accept=".pdf,.zip"
                            onChange={handleFileChange}
                            multiple
                            disabled={isUploading} // Disable input when uploading
                          />
                        </Typography>
                        {file.length > 0 && (
                          <Typography
                            sx={{
                              fontWeight: 500,
                              mt: "20px",
                              lineHeight: { xs: "100%", md: "34px" },
                              fontSize: { xs: "14px", md: "22px" },
                              color: "#FDF9E0",
                            }}
                          >
                            Total Files Selected: {file.length}
                          </Typography>
                        )}
                      </Paper>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                          marginTop: 2,
                        }}
                      >
                        {error && (
                          <Typography
                            color="error"
                            variant="body2"
                            sx={{ textAlign: "center" }}
                          >
                            {error}
                          </Typography>
                        )}
                        {warning && (
                          <Typography
                            variant="body2"
                            sx={{ textAlign: "center", color: "orange" }}
                          >
                            {warning}
                          </Typography>
                        )}
                        <Box sx={{ textAlign: "center" }}>
                          <Button
                            variant="contained"
                            onClick={handleUpload}
                            disabled={isUploading || !file}
                            sx={{
                              "&.Mui-disabled": {
                                backgroundColor: "#333",
                                color: "#000",
                              },
                              color: "#FDF9E0",
                              borderRadius: "8px",
                              mt: "30px",
                            }}
                          >
                            {isUploading ? "Processing..." : "Upload Resume"}
                          </Button>
                        </Box>
                        <Box sx={{ textAlign: "center", marginTop: 2 }}>
                          <Button
                            variant="outlined"
                            onClick={handleClearFiles}
                            sx={{
                              color: "#FDF9E0",
                              borderColor: "#FDF9E0",
                              borderRadius: "8px",
                              textTransform: "none",
                            }}
                          >
                            Clear Files
                          </Button>
                        </Box>
                        {/* <Typography
                          variant="body2"
                          sx={{
                            textAlign: "center",
                            mt: "20px",
                            color: "#FDF9E0",
                          }}
                        >
                          Resume parsing may have errors. Retry or refine on{" "}
                          <Link
                            href={`${process.env.REACT_APP_TECHWORTH_URL}`}
                            // target="_blank"
                            rel="noopener noreferrer"
                            sx={{ fontWeight: "bold" }}
                          >
                            TrueSelfy TechWorth.
                          </Link>
                        </Typography> */}
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        textAlign: "center",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Loading />
                      {/* <Typography
                        variant="body2"
                        sx={{
                          textAlign: "center",
                          mt: "10px",
                          color: "#FDF9E0",
                        }}
                      >
                        Resume parsing may have errors. Retry or refine on{" "}
                        <Link
                          href={`${process.env.REACT_APP_TECHWORTH_URL}`}
                          // target="_blank"
                          rel="noopener noreferrer"
                          sx={{ fontWeight: "bold" }}
                        >
                          TrueSelfy TechWorth.
                        </Link>
                      </Typography> */}
                      <Paper
                        elevation={3}
                        sx={{
                          padding: 2,
                          mt: "20px",
                          width: "100%",
                          textAlign: "center",
                          border: "2px dashed #278C37",
                          borderRadius: "8px",
                          // backgroundColor: file ? "#278C37" : "#f9f9f9",
                          background: "rgba(39, 140, 55, 0.1)",
                        }}
                      >
                        {file && (
                          <Typography variant="body1" sx={{ color: "#FDF9E0" }}>
                            File Selected: {file.map((f) => f.name).join(", ")}
                          </Typography>
                        )}
                      </Paper>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export default ResumeUploadRecruiter;
